import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Trophy, Flame, Star } from "lucide-react";

type CelebrateDetail = {
  type?: "xp" | "level" | "badge" | "streak" | "mission";
  title?: string;
  message?: string;
};

const ICONS = {
  xp: Star,
  level: Trophy,
  badge: Trophy,
  streak: Flame,
  mission: Sparkles,
} as const;

/**
 * Global gamification celebration listener.
 * Fire from anywhere with:
 *   window.dispatchEvent(new CustomEvent("makai:celebrate", {
 *     detail: { type: "level", title: "Level 7!", message: "+250 XP" }
 *   }))
 */
export function CelebrationListener() {
  const [burst, setBurst] = useState<CelebrateDetail | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CelebrateDetail>).detail ?? {};
      const type = detail.type ?? "xp";
      const Icon = ICONS[type] ?? Sparkles;
      toast.success(detail.title ?? "Nice work!", {
        description: detail.message,
        icon: <Icon className="h-4 w-4 text-primary" />,
        duration: 3500,
      });
      setBurst(detail);
      setTimeout(() => setBurst(null), 1400);
    };
    window.addEventListener("makai:celebrate", handler);
    return () => window.removeEventListener("makai:celebrate", handler);
  }, []);

  if (!burst) return null;
  const Icon = ICONS[burst.type ?? "xp"] ?? Sparkles;
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center"
    >
      <div className="animate-in fade-in zoom-in-50 duration-500 rounded-full bg-primary/15 p-10 backdrop-blur-sm">
        <Icon className="h-16 w-16 text-primary drop-shadow-[0_0_20px_color-mix(in_oklab,var(--primary)_60%,transparent)]" />
      </div>
    </div>
  );
}

/** Programmatic helper. */
export function celebrate(detail: CelebrateDetail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("makai:celebrate", { detail }));
}
